/**
 * Professional tax schedules as DATA (ADR 0019).
 *
 * A state's professional tax is one default table of bands plus, where the state says so, VARIANTS: a table that
 * applies instead of the default to a stated gender and/or to stated months (a gender-specific threshold, a
 * February top-up, a half-yearly instalment). Nothing here names a state, a gender or a month that matters: which
 * variants exist, for whom and in which months is what a jurisdiction's schedule says, recorded as data with its
 * source, and changed by publishing a new version, never by editing an engine.
 *
 * SEMANTICS (identical in the rule engine and in the legacy stored configuration):
 *   - variants are tried IN ORDER; the first whose conditions all hold supplies the table;
 *   - a variant holds when the employee's gender is one of `genders` (if given) AND the pay period's month is one
 *     of `months` (if given);
 *   - a variant with neither condition would always hold, so it is refused: that is the default table;
 *   - when nothing holds, the default table applies.
 *
 * A gender is only ever compared when a variant names one. An employee whose gender is not recorded is then NOT
 * guessed at: the caller reports the missing fact (invariant: absent is not false). A schedule with no gender
 * variants never asks for gender at all.
 *
 * RN-safe: no Node built-ins, no I/O.
 */

import { z } from 'zod';

/** A gender key, as `ptGenderKey` produces it: lower-case letters and underscores. */
export const PT_GENDER_KEY_PATTERN = /^[a-z][a-z_]{0,29}$/;

/** Calendar month 1–12 of the pay period's END date, read in the legal entity's own timezone. */
export const ptMonthSchema = z.number().int().min(1).max(12);

/**
 * The key a person's recorded gender is compared by: trimmed, lower-cased, spaces and hyphens folded to `_`.
 * `null` when nothing is recorded (an empty string is not a record). This does not decide which keys a schedule
 * recognises; the schedule lists them.
 */
export function ptGenderKey(raw: string | null | undefined): string | null {
  const key = (raw ?? '')
    .trim()
    .toLowerCase()
    .replace(/[\s-]+/g, '_');
  return key.length > 0 ? key : null;
}

/** The conditions of one variant. At least one is required (see `ptVariantConditionProblems`). */
export const ptVariantConditionShape = {
  /** Shown to a reviewer and in the trace, e.g. "February top-up". */
  label: z.string().min(1).max(120).optional(),
  genders: z.array(z.string().regex(PT_GENDER_KEY_PATTERN)).min(1).max(10).optional(),
  months: z.array(ptMonthSchema).min(1).max(12).optional(),
} as const;

export interface PtVariantConditions {
  readonly genders?: readonly string[] | undefined;
  readonly months?: readonly number[] | undefined;
}

/** What is wrong with a variant's conditions, in words (empty when sound). */
export function ptVariantConditionProblems(v: PtVariantConditions): string[] {
  const problems: string[] = [];
  if (!v.genders && !v.months) {
    problems.push(
      'a variant needs at least one condition (genders and/or months); a table for everyone is the default table',
    );
  }
  if (v.genders && new Set(v.genders).size !== v.genders.length) {
    problems.push('genders lists the same value more than once');
  }
  if (v.months && new Set(v.months).size !== v.months.length) {
    problems.push('months lists the same month more than once');
  }
  return problems;
}

/** Refinement for a variant object, so a stored config and a pack definition refuse the same mistakes. */
export function refinePtVariantConditions(v: PtVariantConditions, ctx: z.RefinementCtx): void {
  for (const message of ptVariantConditionProblems(v)) {
    ctx.addIssue({ code: z.ZodIssueCode.custom, message });
  }
}

/** Whether a variant applies to this employee-month. `gender` is a `ptGenderKey`; `null` = not recorded. */
export function ptVariantApplies(
  v: PtVariantConditions,
  ctx: { readonly month: number; readonly gender: string | null },
): boolean {
  if (v.months && !v.months.includes(ctx.month)) return false;
  if (v.genders) {
    // Not recorded cannot satisfy a gender condition; the caller must have refused to get here (see
    // `ptVariantsNeedGender`), so this is a defence, not the mechanism.
    if (ctx.gender === null || !v.genders.includes(ctx.gender)) return false;
  }
  return true;
}

/** Whether any variant is conditional on gender, i.e. whether the schedule needs the employee's gender at all. */
export const ptVariantsNeedGender = (variants: readonly PtVariantConditions[]): boolean =>
  variants.some((v) => v.genders !== undefined);

/** Whether any variant is conditional on the month, i.e. whether the schedule needs the pay period's month at all. */
export const ptVariantsNeedMonth = (variants: readonly PtVariantConditions[]): boolean =>
  variants.some((v) => v.months !== undefined);
